import React, { useContext, useState, useEffect, useRef, useMemo } from "react";
import { Line } from "react-chartjs-2";
import "chart.js/auto";
import "chartjs-adapter-moment";
import { ChartHeader, ZoomControls, applyChartTheme, CHART_THEME, sectionLineDataset, hoverGuidePlugin } from "../../Concession/charts/chartTheme";
import zoomPlugin from "chartjs-plugin-zoom";
import annotationPlugin from 'chartjs-plugin-annotation';
import { Chart as ChartJS } from "chart.js";
import AuthContext from "../../../contexts/auth-context";
import { PHASES, TRAFFIC_LINE } from "../retailPalette";

ChartJS.register(zoomPlugin, annotationPlugin);

// Function to convert game start time to annotation timestamp
const convertGameStartTimeToAnnotation = (gameDate, gameStartTime) => {
  if (!gameDate || !gameStartTime) return null;
  
  const [hours, minutes] = gameStartTime.split(':').map(Number);
    
  // Create the annotation time using today's date and the game start time
  // Since this chart uses artificial timestamps starting at 8 AM, we need to map
  // the game start time to the same artificial timeline
  const today = new Date();
  const baseTime = new Date(today);
  baseTime.setHours(8, 0, 0, 0); // Start at 8 AM like the chart data
  
  // Calculate business minutes for the game start time (same logic as chart data)
  let businessMinutes;
  if (hours >= 8) {
    // 8 AM or later (current day business hours)
    businessMinutes = (hours - 8) * 60 + minutes;
  } else if (hours < 3) {
    // 12 AM - 2:59 AM (end of previous day's business day)
    businessMinutes = (16 * 60) + (hours * 60) + minutes;
  } else {
    // 3 AM - 7:59 AM (beginning of next business day)
    businessMinutes = (19 * 60) + ((hours - 3) * 60) + minutes;
  }
    
  // Add the business minutes to create the artificial timestamp
  const annotationTime = new Date(baseTime.getTime() + (businessMinutes * 60 * 1000));
    
  return annotationTime.toISOString();
};

const CounterTrafficChart = ({ trafficDataSets, currentGame, dayType, title, subtitle, footer, showPregame = true }) => {

  const authCtx = useContext(AuthContext);
  const [dataValues, setDataValues] = useState([]);
  const [loading, setLoading] = useState(true);
  const chartRef = useRef(null);

  // Function to trim data after 2 hours of consecutive identical count values
  const trimDataAfterFlatPeriod = (sortedDataSet) => {
    if (sortedDataSet.length === 0) return sortedDataSet;

    let consecutiveStart = 0;
    let currentValue = sortedDataSet[0].count;
    let consecutiveCount = 1;

    for (let i = 1; i < sortedDataSet.length; i++) {
      if (sortedDataSet[i].count === currentValue) {
        consecutiveCount++;
      } else {
        // Value changed, reset tracking
        currentValue = sortedDataSet[i].count;
        consecutiveStart = i;
        consecutiveCount = 1;
      }

      // Check if we have 2 hours (120 minutes) of consecutive identical values
      if (consecutiveCount > 1) {
        const startTime = sortedDataSet[consecutiveStart].businessMinutes;
        const currentTime = sortedDataSet[i].businessMinutes;
        const duration = currentTime - startTime;

        if (duration >= 120) { // 120 minutes = 2 hours
          // Trim everything after this point (keep the 2-hour buffer)
          return sortedDataSet.slice(0, i + 1);
        }
      }
    }

    // No 2-hour flat period found, return original dataset
    return sortedDataSet;
  };

  useEffect(() => {
    setLoading(true);

    const processedTrafficData = [];

    // Process traffic data using forEach
    trafficDataSets.forEach((dataSet) => {
      // First, sort the dataset by business day order
      const sortedDataSet = dataSet
        .filter(item => item.timestamp !== "00:00")
        .map(item => {
          const [datePart, timePart] = item.timestamp.split('T');
          const [hours, minutes] = timePart.split(':').map(Number);
          
          // Calculate business day minutes for proper sorting
          // This should be ok since our window is always from 8am to 3am 
          // Any counts after 3am will be lost in the frontend effectively
          // Backend sorts the data from database anyways using 8am to 3am as the window
          // Data should always be good
          let businessMinutes;
          if (hours >= 8) {
            // 8 AM or later (current day business hours)
            businessMinutes = (hours - 8) * 60 + minutes;
          } else if (hours < 3) {
            // 12 AM - 2:59 AM (end of previous day's business day)
            businessMinutes = (16 * 60) + (hours * 60) + minutes;
          } else {
            // 3 AM - 7:59 AM (beginning of next business day)
            businessMinutes = (19 * 60) + ((hours - 3) * 60) + minutes;
          }
          
          return { ...item, businessMinutes };
        })
        .sort((a, b) => a.businessMinutes - b.businessMinutes);

      // Apply trimming logic to remove data after 2 hours of flat values
      const trimmedDataSet = trimDataAfterFlatPeriod(sortedDataSet);

      const processedDataSet = [];

      trimmedDataSet.forEach((item, index) => {
        // Create artificial timestamps that maintain business day order
        const today = new Date();
        const baseTime = new Date(today);
        baseTime.setHours(8, 0, 0, 0); // Start at 8 AM
        
        // Add the business minutes to create proper chronological order
        const chartTime = new Date(baseTime.getTime() + (item.businessMinutes * 60 * 1000));
        const isoDateStr = chartTime.toISOString();

        processedDataSet.push({
          x: isoDateStr,
          y: item.clear ? 0 : item.count,
          clear: item.clear,
        });
      });

      processedTrafficData.push(processedDataSet);
    });

    setDataValues(processedTrafficData);
    setLoading(false);
  }, [trafficDataSets]);

  const maxValue = Math.max(
    ...dataValues.flatMap((dataSet) => dataSet.map((item) => item.y))
  );

  // Create game start annotation with useMemo - only for game days
  const gameStartAnnotation = useMemo(() => {
    if (dayType !== 'game') return null;
    return currentGame?.date && currentGame?.gameStartTime ? 
      convertGameStartTimeToAnnotation(currentGame.date, currentGame.gameStartTime) : null;
  }, [dayType, currentGame?.date, currentGame?.gameStartTime]);

  // Shaded phase bands (pregame, gates open, game), same colors as the phase cards
  const phaseBands = useMemo(() => {
    if (dayType !== 'game' || !currentGame?.date || !gameStartAnnotation) return [];
    const at = (t) => (t ? convertGameStartTimeToAnnotation(currentGame.date, t) : null);
    const gates = at(currentGame.gatesOpen);
    const band = (phase, xMin, xMax) => ({
      type: 'box',
      xScaleID: 'x',
      ...(xMin ? { xMin } : {}),
      ...(xMax ? { xMax } : {}),
      backgroundColor: phase.tint,
      borderWidth: 0,
      drawTime: 'beforeDatasetsDraw',
      label: {
        display: true,
        content: phase.label,
        position: { x: 'start', y: 'start' },
        color: phase.color,
        font: { size: 11, weight: '600', family: 'Outfit' },
        padding: { top: 6, left: 8 },
      },
    });
    if (!gates) return [band(PHASES.game, gameStartAnnotation, null)];
    return [
      // No pregame entries for this game: skip the Pregame band (it would be squeezed against Gates open)
      ...(showPregame ? [band(PHASES.pregame, null, gates)] : []),
      band(PHASES.gates, gates, gameStartAnnotation),
      band(PHASES.game, gameStartAnnotation, null),
    ];
  }, [dayType, currentGame?.date, currentGame?.gatesOpen, gameStartAnnotation, showPregame]);

  const chartData = {
    labels: dataValues.length > 0 ? dataValues[0].map((item) => item.x) : [],
    datasets: dataValues.map((dataSet, index) => ({
      label: 'People Count', // Changed from empty string for legend
      data: dataSet.map((item) => ({ x: item.x, y: item.clear ? 0 : item.y })),
      ...sectionLineDataset(TRAFFIC_LINE),
    })),
  };

  // Gets primary text color for chart
  const primaryTextColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-txt').trim();

  // Memoize chartOptions to prevent unnecessary recreations
  const chartOptions = useMemo(() => ({
    scales: {
      x: {
        title: {
          color: primaryTextColor,
          display: true,
          text: "Time",
          padding: { top: 0, bottom: 10 },
        },
        type: "time",
        time: {
          unit: "hour",
          displayFormats: {
            hour: "h A",
          },
        },
        grid: {
          display: false,
        },
        ticks: {
          source: "auto",
          color: primaryTextColor,
          autoSkip: true,
          maxRotation: 0,
          minRotation: 0,
        },
      },
      y: {
        display: true,
        title: {
          color: primaryTextColor,
          display: true,
          text: "People",
          padding: { top: 0, bottom: 10 },
        },
        grid: {
          display: true,
        },
        ticks: {
          color: primaryTextColor,
          precision: 0,
          beginAtZero: true,
          stepSize: (maxValue < 100) ? 20 : 100,
          callback: function (value) {
            if (value % 100 === 0) {
              return value;
            }
          },
        },
        max: (maxValue < 100) ? 100 : Math.max(Math.ceil(maxValue / 100) * 100) + 100
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false,
        callbacks: {
          title: (context) => {
            if (context.length == 0) {
              return "";
            } else {
              const labelParts = context[0].label.split(',');  
              const pulledTime = labelParts.length > 2 ? labelParts[2].trim() : '';  // grab the time not date

              const timeFormatter = new Date(`2000-01-01 ${pulledTime}`);
              const formattedTime = timeFormatter.toLocaleTimeString([], {
                hour: 'numeric',
                minute: '2-digit'
              });

              return `${formattedTime}`;
            }
          },
          label: (context) => ` ${context.raw.y}`,
        },
      },
      legend: {
        display: true,
        position: 'top',
        labels: {
          color: primaryTextColor,
          usePointStyle: true,
          pointStyle: 'rectRounded',
          generateLabels: function(chart) {
            // Get default labels for the datasets
            const defaultLabels = ChartJS.defaults.plugins.legend.labels.generateLabels(chart);
            
            // Add game start indicator if we have one - put it FIRST
            if (gameStartAnnotation) {
              const gameStartLabel = {
                text: "Game Start",//authCtx.league === "mlb" ? "First Pitch" : authCtx.league === "nfl" ? "Kick Off" : "Game Start",
                fillStyle: 'transparent',
                strokeStyle: CHART_THEME.gameStart,
                lineWidth: 3,
                lineDash: [8, 2],
                pointStyle: 'line',
                hidden: false,
                index: -1 // Special index to identify this as non-clickable
              };
              
              // Put game start at the beginning
              return [gameStartLabel, ...defaultLabels];
            }
            
            return defaultLabels;
          }
        },
        onClick: function(evt, legendItem, legend) {
          // Only handle clicks for dataset items (not game start)
          if (legendItem.index !== -1) {
            // Use the default legend click behavior for dataset items
            ChartJS.defaults.plugins.legend.onClick.call(this, evt, legendItem, legend);
          }
          // Do nothing for game start (index === -1)
        }
      },
      zoom: {
        limits: {
          x: { min: 'original', max: 'original', minRange: 60 * 60 * 1000 },
        },
        zoom: {
          wheel: {
            enabled: true,
          },
          pinch: {
            enabled: true
          },
          mode: 'x',
        },
        pan: {
          enabled: true,
          mode: 'x',
        },
      },
      annotation: {
        annotations: gameStartAnnotation ? [
          ...phaseBands,
          {
            type: 'line',
            mode: 'vertical',
            scaleID: 'x',
            value: gameStartAnnotation,
            borderColor: CHART_THEME.gameStart,
            borderWidth: 2,
            borderDash: [8, 4]
          }
        ] : []
      },
    },
  }), [gameStartAnnotation, phaseBands, maxValue, primaryTextColor, authCtx.league]);

  if (!loading) {
    return (
      <div className="graph panel-2 cx-chart-card">
        <ChartHeader
          title={title || "Traffic over time"}
          subtitle={subtitle || "People entering the store across the day. Scroll to zoom, drag to pan."}
          controls={<ZoomControls chartRef={chartRef} />}
        />
        <div style={{ position: 'relative', height: '300px', marginTop: '8px' }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflowX: 'auto',
          }}>
            <div style={{ width: '100%', height: '100%' }}>
              <Line 
                key={currentGame?.date || 'no-game'} 
                ref={chartRef} 
                data={chartData} 
                options={applyChartTheme(chartOptions)}
              plugins={[hoverGuidePlugin]}
              />
            </div>
          </div>
        </div>
        {footer && <div className="rx-chart-footer">{footer}</div>}
      </div>
    );
  }

  return <p>Loading...</p>;
};

export default CounterTrafficChart;